import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePostsStore } from '../stores/postsStore';
import { useDialogStore } from '../stores/dialogStore';
import { useGalleriesStore } from '../stores/galleriesStore';
import { useLayoutsStore } from '../stores/layoutsStore';
import RichTextEditorWithMentions from './RichTextEditorWithMentions';
import Reactions from './Reactions';
import Reply from './Reply';
import ProfileAvatar from './ProfileAvatar';
import GalleryRenderer from './GalleryRenderer';
import ProtectedImage from './ProtectedImage';
import ProtectedVideo from './ProtectedVideo';

const Post = ({ post, viewingProfileId = null, openReplies = false }) => {
  const { accessToken, profile } = useAuthStore();
  const { updatePost, deletePost, togglePin, fetchReplies } = usePostsStore();
  const { confirm, alert } = useDialogStore();
  const {
    createGallery,
    deleteGallery,
    fetchGallery,
    addMedia,
    deleteMedia,
    updateGallery,
    reorderMedia
  } = useGalleriesStore();
  const { mediaLayouts, fetchMediaLayouts } = useLayoutsStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editedBody, setEditedBody] = useState(post.body);
  const [editedIsHero, setEditedIsHero] = useState(post.is_hero || false);
  const [showReplies, setShowReplies] = useState(openReplies);
  const [isCreatingReply, setIsCreatingReply] = useState(false);

  // Auto-open replies if requested
  useEffect(() => {
    if (openReplies && accessToken) {
      setShowReplies(true);
      fetchReplies(accessToken, post.id);
    }
  }, [openReplies, accessToken, post.id, fetchReplies]);
  const [localGalleryMedia, setLocalGalleryMedia] = useState([]);
  const [galleryLayout, setGalleryLayout] = useState(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [viewingMedia, setViewingMedia] = useState(null);

  const isOwner = profile?.id === post.profile_id;

  // Convert @mentions to clickable links
  const convertMentionsToLinks = (text) => {
    if (!text) return '';

    // Replace @DisplayName with clickable links (without the @)
    // Match @word characters (letters, numbers, underscores)
    return text.replace(/@(\w+)/g, (match, displayName) => {
      // Find the profile from post tags to get the profile ID
      const taggedProfile = post.tags?.find(tag => tag.display_name === displayName);
      if (taggedProfile && taggedProfile.id) {
        return `<a href="/profile/${taggedProfile.id}/page/dashboard" class="text-primary-600 dark:text-primary-400 hover:underline font-semibold">${displayName}</a>`;
      }
      // Fallback if no tag found (shouldn't happen)
      return `<span class="text-primary-600 dark:text-primary-400 font-semibold">${displayName}</span>`;
    });
  };

  // Load media layouts
  useEffect(() => {
    if (accessToken) {
      fetchMediaLayouts(accessToken);
    }
  }, [accessToken, fetchMediaLayouts]);

  // Load gallery media when post has gallery
  useEffect(() => {
    const loadGallery = async () => {
      if (post.gallery && post.gallery.id && accessToken) {
        const result = await fetchGallery(post.gallery.id, accessToken);
        if (result.success && result.gallery) {
          setLocalGalleryMedia(result.gallery.media || []);
          setGalleryLayout(result.gallery.layout_code);
        }
      } else {
        // Clear gallery media if post doesn't have a gallery
        setLocalGalleryMedia([]);
        setGalleryLayout(null);
      }
    };
    loadGallery();
  }, [post.gallery, accessToken, fetchGallery]);

  const handleEdit = () => {
    setEditedBody(post.body);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedBody(post.body);
  };

  const handleSaveEdit = async () => {
    if (!editedBody.trim()) {
      alert('Error', 'Post body cannot be empty');
      return;
    }

    const result = await updatePost(accessToken, post.id, {
      body: editedBody,
      is_hero: editedIsHero ? 1 : 0
    });

    if (result.success) {
      setIsEditing(false);
    } else {
      alert('Error', result.message || 'Failed to update post');
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm(
      'Delete Post',
      'Are you sure you want to delete this post? This action cannot be undone.'
    );

    if (!confirmed) return;

    const result = await deletePost(accessToken, post.id);

    if (!result.success) {
      alert('Error', result.message || 'Failed to delete post');
    }
  };

  const handleTogglePin = async () => {
    const result = await togglePin(accessToken, post.id);

    if (!result.success) {
      alert('Error', result.message || 'Failed to toggle pin');
    }
  };

  const handleShowReplies = async () => {
    if (!showReplies && (!post.replies || post.replies.length === 0)) {
      await fetchReplies(accessToken, post.id);
    }
    setShowReplies(!showReplies);
  };

  // Gallery handlers
  const handleGalleryLayoutChange = async (layoutCode) => {
    if (!post.gallery?.id) return;

    // Optimistically update UI
    setGalleryLayout(layoutCode);

    const result = await updateGallery(post.gallery.id, { layout_code: layoutCode }, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to update gallery layout');
      // Revert on error
      const galleryResult = await fetchGallery(post.gallery.id, accessToken);
      if (galleryResult.success && galleryResult.gallery) {
        setGalleryLayout(galleryResult.gallery.layout_code);
      }
    }
  };

  const handleAddMediaToGallery = async (files) => {
    if (!post.gallery?.id || !files || files.length === 0) return;

    setUploadingGallery(true);

    const result = await addMedia(post.gallery.id, files, accessToken);

    if (!result.success) {
      await alert('Error', result.error || 'Failed to upload media');
    } else {
      // Refresh gallery to show new media
      const galleryResult = await fetchGallery(post.gallery.id, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }

    setUploadingGallery(false);
  };

  const handleDeleteMediaFromGallery = async (mediaId) => {
    if (!post.gallery?.id) return;

    const confirmed = await confirm(
      'Delete Media',
      'Are you sure you want to delete this media?'
    );
    if (!confirmed) return;

    // Close modal if this media is currently open
    if (viewingMedia?.id === mediaId) {
      setViewingMedia(null);
    }

    const result = await deleteMedia(post.gallery.id, mediaId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete media');
    } else {
      // Refresh gallery
      const galleryResult = await fetchGallery(post.gallery.id, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  const handleDeleteGallery = async () => {
    if (!post.gallery?.id) return;

    const confirmed = await confirm(
      'Delete Gallery',
      'Are you sure you want to delete this gallery and all its media?'
    );
    if (!confirmed) return;

    const result = await deleteGallery(post.gallery.id, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete gallery');
    } else {
      // Refresh page to update post
      window.location.reload();
    }
  };

  const handleReorderMedia = async (mediaOrder) => {
    if (!post.gallery?.id) return;

    const result = await reorderMedia(post.gallery.id, mediaOrder, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to reorder media');
      // Refresh gallery to revert optimistic update
      const galleryResult = await fetchGallery(post.gallery.id, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  const handleViewMedia = (media) => {
    setViewingMedia(media);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="surface rounded-lg shadow-lg p-6">
      {/* Post Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <ProfileAvatar
            profileId={post.profile_id}
            displayName={post.author_display_name}
            size="md"
          />
          <div>
            <h3 className="font-semibold text-primary">
              {post.author_display_name}
              {/* Show "posted on" when author posted on someone else's timeline AND we're not viewing that timeline */}
              {post.profile_id !== post.post_on_profile_id &&
               post.post_on_profile_id !== viewingProfileId &&
               post.posted_on_profile_name && (
                <>
                  <span className="text-secondary font-normal"> → </span>
                  <a
                    href={`/profile/${post.post_on_profile_id}/dashboard`}
                    className="hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = `/profile/${post.post_on_profile_id}/dashboard`;
                    }}
                  >
                    {post.posted_on_profile_name}
                  </a>
                </>
              )}
            </h3>
            <p className="text-xs text-secondary" title={new Date(post.created_at).toLocaleString()}>
              {formatDate(post.created_at)}
              {post.updated_at && post.updated_at !== post.created_at && ' (edited)'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Only show pin icon when viewing the post author's own timeline */}
          {post.is_pinned && post.profile_id === viewingProfileId ? (
            <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 384 512" title="Pinned">
              <path d="M32 32C32 14.3 46.3 0 64 0H320c17.7 0 32 14.3 32 32s-14.3 32-32 32H290.5l11.4 148.2c36.7 19.9 65.7 53.2 79.5 94.7l1 3c3.3 9.8 1.6 20.5-4.4 28.8s-15.7 13.3-26 13.3H32c-10.3 0-19.9-4.9-26-13.3s-7.7-19.1-4.4-28.8l1-3c13.8-41.5 42.8-74.8 79.5-94.7L93.5 64H64C46.3 64 32 49.7 32 32zM160 384h64v96c0 17.7-14.3 32-32 32s-32-14.3-32-32V384z"/>
            </svg>
          ) : null}

          {isOwner && (
            <div className="relative group">
              <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
                <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>

              <div className="absolute right-0 mt-2 w-48 surface rounded-lg shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={handleEdit}
                  className="block w-full text-left px-4 py-2 text-sm text-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Edit
                </button>
                <button
                  onClick={handleTogglePin}
                  className="block w-full text-left px-4 py-2 text-sm text-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {post.is_pinned ? 'Unpin' : 'Pin'}
                </button>
                <button
                  onClick={handleDelete}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Post Body */}
      {isEditing ? (
        <div className="mb-4">
          <RichTextEditorWithMentions
            value={editedBody}
            onChange={setEditedBody}
            postId={post.id}
            placeholder="What's on your mind?"
            isHero={editedIsHero}
            onHeroChange={setEditedIsHero}
          />

          {/* Gallery in Edit Mode - only show if post already has gallery */}
          {post.gallery && post.gallery.id && (
            <div className="mt-4">
              <GalleryRenderer
                media={localGalleryMedia}
                layout={galleryLayout}
                editMode={true}
                onLayoutChange={handleGalleryLayoutChange}
                onAddMedia={handleAddMediaToGallery}
                onDeleteMedia={handleDeleteMediaFromGallery}
                onDeleteGallery={handleDeleteGallery}
                onViewMedia={handleViewMedia}
                onReorderMedia={handleReorderMedia}
                availableLayouts={mediaLayouts.filter(l => l.is_active && l.type_id === 2)}
                uploading={uploadingGallery}
              />
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleSaveEdit}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`prose dark:prose-invert max-w-none ${
              post.is_hero
                ? 'text-center text-3xl font-bold my-32 text-primary'
                : 'mb-4 text-primary'
            }`}
            dangerouslySetInnerHTML={{ __html: convertMentionsToLinks(post.body) }}
          />

          {/* Gallery in View Mode */}
          {post.gallery && post.gallery.id && localGalleryMedia.length > 0 ? (
            <div className="mb-4">
              <GalleryRenderer
                media={localGalleryMedia}
                layout={galleryLayout}
                editMode={false}
                onViewMedia={handleViewMedia}
              />
            </div>
          ) : null}
        </>
      )}

      {/* Reactions */}
      <Reactions
        postId={post.id}
        totalLikes={post.total_likes || 0}
        totalDislikes={post.total_dislikes || 0}
        userLikeValue={post.user_like_value}
        userDislikeValue={post.user_dislike_value}
      />

      {/* Replies Section */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <button
            onClick={handleShowReplies}
            className="text-sm text-secondary hover:text-primary flex items-center gap-1"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
            {post.reply_count || 0} {post.reply_count === 1 ? 'reply' : 'replies'}
          </button>

          <button
            onClick={() => setIsCreatingReply(!isCreatingReply)}
            className="text-sm text-primary hover:text-primary-700"
          >
            {isCreatingReply ? 'Cancel' : 'Reply'}
          </button>
        </div>

        {/* Create Reply */}
        {isCreatingReply && (
          <div className="mt-4">
            <Reply
              postId={post.id}
              parentReplyId={null}
              depth={0}
              onReplyCreated={() => {
                setIsCreatingReply(false);
                setShowReplies(true);
              }}
            />
          </div>
        )}

        {/* Show Replies */}
        {showReplies && post.replies && post.replies.length > 0 ? (
          <div className="mt-4 space-y-4">
            {post.replies.map((reply) => (
              <Reply
                key={reply.id}
                postId={post.id}
                reply={reply}
                depth={reply.depth}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* View Media Modal */}
      {viewingMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={() => setViewingMedia(null)}
        >
          <button
            onClick={() => setViewingMedia(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-6xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            {viewingMedia.kind_code === 'IMAGE' ? (
              <ProtectedImage
                uuid={viewingMedia.uuid}
                alt={viewingMedia.storage_key}
                className="max-w-full max-h-[90vh] object-contain"
              />
            ) : (
              <ProtectedVideo
                uuid={viewingMedia.uuid}
                controls={true}
                autoPlay={true}
                className="max-w-full max-h-[90vh]"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Post;
